import React, { useCallback, useMemo } from "react";
import { Badge, Button, useToast } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { CrudPage } from "../components/CrudPage";
import { UserForm } from "../components/forms";
import { auth, roles as rolesApi, users } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { ACCESS_GRANT_LABEL_MAP } from "../constants/accessGrants";
import { Role, TableFilterDefinition } from "../types";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../utils/userIdentity";

export const UsersPage = () => {
  const { isSuperAdmin } = useAuth();
  const toast = useToast();
  const { data: rolesResponse } = useQuery(["roles", "user-table-filters"], () =>
    rolesApi.getAll(1, 100),
  );
  const roleItems = useMemo<Role[]>(
    () => (rolesResponse as any)?.roles || [],
    [rolesResponse],
  );

  const handleImpersonate = useCallback(
    async (userId: string) => {
      try {
        const response = await auth.impersonateUser(userId);
        const loginLink = `${window.location.origin}/omidex?impersonationToken=${encodeURIComponent(response.token)}`;
        await navigator.clipboard.writeText(loginLink);
        toast({
          title: "لینک ورود کپی شد",
          description: "لینک ورود به حساب کاربر در کلیپ‌بورد شما قرار گرفت",
          status: "success",
          duration: 3500,
          isClosable: true,
        });
      } catch (error: any) {
        toast({
          title: "خطا در ایجاد لینک ورود",
          description:
            error?.response?.data?.message || error?.message || "خطای ناشناخته",
          status: "error",
          duration: 3500,
          isClosable: true,
        });
      }
    },
    [toast],
  );

  const columns = useMemo(
    () => [
      {
        header: "نام و نام خانوادگی",
        accessor: "fullName",
        isSortable: true,
      },
      {
        header: "شناسه هویتی",
        accessor: "identifier",
        render: (item: any) =>
          `${getUserIdentifierLabel(item)}: ${getUserIdentifier(item) || "—"}`,
      },
      { header: "موبایل", accessor: "mobile", isSortable: true },
      {
        header: "وضعیت سجام",
        accessor: "isSejamVerified",
        isSortable: true,
        render: (item: any) => (
          <Badge colorScheme={item.isSejamVerified ? "green" : "red"}>
            {item.isSejamVerified ? "تایید شده" : "تایید نشده"}
          </Badge>
        ),
      },
      {
        header: "نقش",
        accessor: "role",
        render: (item: any) => item.role?.label || item.role?.value || "—",
      },
      {
        header: "دسترسی‌های ویژه",
        accessor: "accessGrants",
        render: (item: any) =>
          item.accessGrants?.length
            ? item.accessGrants
                .map((grant: string) => ACCESS_GRANT_LABEL_MAP[grant] || grant)
                .join("، ")
            : "—",
      },
      {
        header: "لینک ورود به حساب",
        accessor: "loginLink",
        render: (item: any) =>
          isSuperAdmin() ? (
            <Button
              size="sm"
              variant="outline"
              borderRadius="full"
              onClick={() => void handleImpersonate(item._id)}
            >
              لینک ورود به حساب
            </Button>
          ) : (
            "—"
          ),
      },
    ],
    [handleImpersonate, isSuperAdmin],
  );
  const filterDefinitions = useMemo<TableFilterDefinition[]>(
    () => [
      {
        key: "fullName",
        label: "نام و نام خانوادگی",
        type: "text",
        placeholder: "جست‌وجوی نام",
      },
      {
        key: "nationalId",
        label: "کد ملی",
        type: "text",
        placeholder: "کد ملی",
      },
      {
        key: "businessID",
        label: "شناسه ملی",
        type: "text",
        placeholder: "شناسه ملی",
      },
      {
        key: "mobile",
        label: "موبایل",
        type: "text",
        placeholder: "شماره موبایل",
      },
      {
        key: "role",
        label: "نقش",
        type: "select",
        options: roleItems.map((role) => ({
          value: role._id,
          label: role.label || role.value,
        })),
      },
      {
        key: "isSejamVerified",
        label: "وضعیت سجام",
        type: "select",
        options: [
          { value: "true", label: "تایید شده" },
          { value: "false", label: "تایید نشده" },
        ],
      },
      {
        key: "createdFrom",
        label: "تاریخ ثبت از",
        type: "date",
        placeholder: "تاریخ شروع",
        usePersianDatePicker: true,
      },
      {
        key: "createdTo",
        label: "تاریخ ثبت تا",
        type: "date",
        placeholder: "تاریخ پایان",
        usePersianDatePicker: true,
      },
    ],
    [roleItems],
  );

  return (
    <CrudPage
      title="کاربران"
      columns={columns}
      queryKey="users"
      resourceName="users"
      api={users}
      FormComponent={UserForm}
      enableDelete={false}
      filterDefinitions={filterDefinitions}
      initialSort={{ field: "createdAt", direction: "desc" }}
    />
  );
};
